import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import TableChartIcon from "@mui/icons-material/TableChart";
import DashboardLayout from "../../layouts/DashboardLayout";
import QRCode from "react-qr-code";
import JsBarcode from "jsbarcode";
import * as bwipjs from "bwip-js";
import {
  getDownloadURL,
  listAll,
  ref,
  getMetadata,
  type ListResult,
  type StorageReference,
} from "firebase/storage";
import { ensureStorage } from "../../firebaseConfig";
import { useUserControls } from "../../context/UserControlsProvider";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import type { ItemSnapshot, Shipment } from "../../types/DB2";
import { formatStatusLabel } from "../../utils/status";

type CodeType =
  | "code128"
  | "qr"
  | "aztec"
  | "codabar"
  | "code39"
  | "code93"
  | "datamatrix"
  | "ean8"
  | "ean13"
  | "itf"
  | "pdf417"
  | "upca"
  | "upce";

type OverlayCornerKey = "tr" | "tl" | "bl" | "br";

type TrainingImage = {
  title: string;
  url: string;
  overlayCornerKey?: OverlayCornerKey;
  preferredCodeType?: CodeType;
};

type SandboxRow = {
  key: string;
  orderLabel: string;
  itemLabel: string;
  destination: string;
  status: ItemSnapshot["status"];
  processed: boolean;
  cabinetNumber?: string;
  itemId: string;
};

const DEFAULT_TEXT = "CABINET-SIM";

const FALLBACK_IMAGE: TrainingImage = {
  title: "Overlay Preview",
  url:
    "https://firebasestorage.googleapis.com/v0/b/cabinet-logger.firebasestorage.app/o/Gemini_Generated_Image_s5dcis5dcis5dcis%20%281%29.png?alt=media&token=914c2bb4-8197-442b-8765-6a7a95fab19f",
};

// Quiet zone padding applied equally left/right around barcodes in overlays
const QUIET_PAD = 18; // pixels
const QUIET_PAD_VERTICAL = 10; // pixels

const cornerMetadataToState: Record<string, OverlayCornerKey> = {
  "top-left": "tl",
  tl: "tl",
  "top_right": "tr",
  "top-right": "tr",
  tr: "tr",
  "bottom-left": "bl",
  bl: "bl",
  "bottom_right": "br",
  "bottom-right": "br",
  br: "br",
};

const codeTypeMetadataToState = (value?: string): CodeType | undefined => {
  if (!value) return undefined;
  const normalized = value.toLowerCase();
  if (normalized === "qr" || normalized === "qrcode") return "qr";
  if (normalized === "code128" || normalized === "barcode") return "code39";
  return undefined;
};

const toTitleCase = (value: string) =>
  formatStatusLabel(value) || value;

const extractNumericRank = (value: string): number => {
  const matches = value.match(/\d+/g);
  if (!matches || matches.length === 0) return Number.POSITIVE_INFINITY;
  const numericSegments = matches
    .map((segment) => {
      const parsed = parseInt(segment, 10);
      return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
    })
    .filter((num) => Number.isFinite(num));
  if (numericSegments.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...numericSegments);
};

const deriveOrderLabel = (orderId: string, item: ItemSnapshot): string => {
  if (item.orderNumber) return item.orderNumber;
  if (item.prefix && orderId.startsWith(item.prefix)) {
    const trimmed = orderId.slice(item.prefix.length).trim();
    if (trimmed) return trimmed;
  }
  const simplified = orderId.replace(/^[A-Za-z]+-/, "").trim();
  return simplified || orderId;
};

type CodeOption =
  | { type: "option"; value: CodeType; label: string }
  | { type: "divider"; key: string };

const CODE_OPTIONS: CodeOption[] = [
  { type: "option", value: "code39", label: "Code 39" },
  { type: "option", value: "qr", label: "QR Code" },
  { type: "option", value: "code128", label: "234 (Code 128)" },
  { type: "divider", key: "formats-divider" },
  { type: "option", value: "aztec", label: "Aztec" },
  { type: "option", value: "codabar", label: "Codabar" },
  { type: "option", value: "code93", label: "Code 93" },
  { type: "option", value: "datamatrix", label: "Data Matrix" },
  // Removed EAN-8 and UPC-E per min 9-digit data policy
  { type: "option", value: "ean13", label: "EAN-13" },
  { type: "option", value: "itf", label: "ITF (Interleaved 2 of 5)" },
  { type: "option", value: "pdf417", label: "PDF417" },
  { type: "option", value: "upca", label: "UPC-A" },
];

const BWIP_BCIDS: Record<Exclude<CodeType, "qr" | "code128">, string> = {
  aztec: "azteccode",
  codabar: "codabar",
  code39: "code39",
  code93: "code93",
  datamatrix: "datamatrix",
  ean8: "ean8",
  ean13: "ean13",
  itf: "interleaved2of5",
  pdf417: "pdf417",
  upca: "upca",
  upce: "upce",
};

const LINEAR_BARCODE_HEIGHT = 118;

const sanitizeDigits = (input: string, requiredLength: number): string => {
  const digitsOnly = input.replace(/\D/g, "");
  const base = digitsOnly.length > 0 ? digitsOnly : "0".repeat(requiredLength);
  if (base.length >= requiredLength) {
    return base.slice(0, requiredLength);
  }
  return base.padEnd(requiredLength, "0");
};

const ean13CheckDigit = (base12: string): string => {
  const digits = base12.replace(/\D/g, "").slice(0, 12);
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const n = parseInt(digits[i], 10) || 0;
    // positions are 0-based from left; even index -> weight 1, odd -> weight 3
    sum += (i % 2 === 0 ? 1 : 3) * n;
  }
  const mod = sum % 10;
  const cd = (10 - mod) % 10;
  return String(cd);
};

const ean8CheckDigit = (base7: string): string => {
  const digits = base7.replace(/\D/g, "").slice(0, 7);
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const n = parseInt(digits[i], 10) || 0;
    // EAN-8 weights: 3,1,3,1,3,1,3 (starting from left)
    sum += (i % 2 === 0 ? 3 : 1) * n;
  }
  const mod = sum % 10;
  const cd = (10 - mod) % 10;
  return String(cd);
};

const ensureEvenLength = (input: string): string => {
  if (input.length % 2 === 0) return input;
  return `${input}0`;
};

const selectControlBaseSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    minHeight: 44,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "divider",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
  },
};

const getStatusChipColor = (
  status: string
): "default" | "success" | "warning" | "error" | "info" => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "delivered":
      return "success";
    case "damaged":
    case "missing":
      return "error";
    case "pending":
      return "warning";
    case "in transit":
      return "info";
    default:
      return "default";
  }
};

const ScanSandbox: React.FC = () => {
  const [text, setText] = useState<string>(DEFAULT_TEXT);
  const [codeType, setCodeType] = useState<CodeType>("code39");
  const overlaySvgRef = useRef<SVGSVGElement | null>(null);
  const barcodeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageBoxRef = useRef<HTMLDivElement | null>(null);
  const [overlayWidth, setOverlayWidth] = useState<number | null>(null);
  const [overlayCorner, setOverlayCorner] = useState<OverlayCornerKey>("tr");
  const [albumImages, setAlbumImages] = useState<TrainingImage[]>([]);
  const [albumIndex, setAlbumIndex] = useState(0);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumError, setAlbumError] = useState<string | null>(null);
  const [nextOverlayCorner, setNextOverlayCorner] = useState<OverlayCornerKey | null>(null);
  const [nextCodeType, setNextCodeType] = useState<CodeType | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [pendingItemIndex, setPendingItemIndex] = useState<number | null>(null);
  // Hide manual value entry in simulator; value comes from selected item/order

  const {
    possibleShipmentIds,
    selectedShipment,
    setSelectedShipmentIndex,
    possibleLocations,
    unloadLocation,
    setUnloadLocation,
    processedGroups,
    unprocessedGroups,
  } = useUserControls();
  const { shipments } = usePrimaryFetchedData();

  const normalizedText = useMemo(() => text.trim() || DEFAULT_TEXT, [text]);
  const currentImage = albumImages[albumIndex] ?? FALLBACK_IMAGE;
  const isQrCode = codeType === "qr";
  const usesJsBarcode = codeType === "code128";
  const usesBwipBarcode = !isQrCode && !usesJsBarcode;
  const renderLinearBarcode = !isQrCode;
  const barcodeValue = useMemo(() => {
    if (isQrCode) return normalizedText;
    if (usesJsBarcode) return normalizedText;
    const base = normalizedText.trim();
    switch (codeType) {
      case "ean8": {
        const b7 = sanitizeDigits(base, 7);
        const value = b7 + ean8CheckDigit(b7);
        return value;
      }
      case "ean13": {
        const b12 = sanitizeDigits(base, 12);
        const value = b12 + ean13CheckDigit(b12);
        return value;
      }
      case "upca": {
        const value = sanitizeDigits(base, 12);
        return value;
      }
      case "upce": {
        // Most renderers expect 6-digit UPC-E (check digit is computed)
        const value = sanitizeDigits(base, 6);
        return value;
      }
      case "itf": {
        const digits = sanitizeDigits(base, Math.max(base.replace(/\D/g, "").length, 2));
        const evenDigits = ensureEvenLength(digits);
        return evenDigits;
      }
      case "codabar":
      case "code39":
      case "code93": {
        let value = base.toUpperCase();
        if (codeType === "codabar") {
          // Ensure start/stop (A-D) for Codabar
          const startsOk = /^[ABCD]/.test(value);
          const endsOk = /[ABCD]$/.test(value);
          const inner = value.replace(/^[ABCD]/, "").replace(/[ABCD]$/, "");
          value = (startsOk ? value[0] : "A") + (inner || "123456") + (endsOk ? value[value.length - 1] : "A");
        }
        return value || "A123456A";
      }
      default:
        return base || "SIM12345";
    }
  }, [normalizedText, codeType, isQrCode, usesJsBarcode]);

  useEffect(() => {
    if (!usesJsBarcode) return;
    const commonOpts = {
      format: "CODE128",
      displayValue: true,
      lineColor: "#111",
      width: 2,
      height: 118,
      margin: 4,
      fontSize: 16,
    } as const;
    try {
      if (overlaySvgRef.current) {
        JsBarcode(overlaySvgRef.current, barcodeValue, {
          ...commonOpts,
          displayValue: false,
          width: 1.5,
          height: 96,
          fontSize: 10,
          margin: 0, // margins handled by overlay padding for equal spacing
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ScanSandbox] Failed to render barcode(s)", err);
    }
  }, [usesJsBarcode, barcodeValue]);

  useEffect(() => {
    if (!usesBwipBarcode) return;
    const canvas = barcodeCanvasRef.current;
    if (!canvas) return;
    try {
      const toCanvas: ((canvas: HTMLCanvasElement | string, opts: Record<string, unknown>) => HTMLCanvasElement) | undefined =
        typeof (bwipjs as any)?.toCanvas === "function" ? (bwipjs as any).toCanvas : undefined;
      if (!toCanvas) return;
      const bcid = BWIP_BCIDS[codeType as Exclude<CodeType, "qr" | "code128">];
      if (!bcid) return;
      toCanvas(canvas, {
        bcid,
        text: barcodeValue,
        scale: 3,
        height: LINEAR_BARCODE_HEIGHT / 8,
        includetext: false,
        backgroundcolor: "FFFFFF",
      });
      canvas.style.height = `${LINEAR_BARCODE_HEIGHT}px`;
      canvas.style.width = "100%";
    } catch (err) {
      console.error("[ScanSandbox] Failed to render bwip barcode", { codeType, err });
    }
  }, [usesBwipBarcode, codeType, barcodeValue]);

  useEffect(() => {
    if (!renderLinearBarcode) {
      setOverlayWidth(null);
      return;
    }
    const measureWidth = () => {
      const imgBox = imageBoxRef.current;
      if (!imgBox) return null;

      let target: SVGSVGElement | HTMLCanvasElement | null = null;
      if (usesJsBarcode) {
        target = overlaySvgRef.current;
      } else if (usesBwipBarcode) {
        target = barcodeCanvasRef.current;
      }
      if (!target) return null;

      try {
        let intrinsic = 0;
        if (target instanceof SVGSVGElement) {
          const bbox = target.getBBox();
          intrinsic = bbox.width + QUIET_PAD * 2;
        } else {
          intrinsic = (target.width || 0) + QUIET_PAD * 2;
        }
        const containerW = imgBox.clientWidth || 0;
        const maxW = containerW * 0.8;
        return Math.round(Math.min(intrinsic, maxW));
      } catch {
        return null;
      }
    };

    const compute = () => {
      const width = measureWidth();
      setOverlayWidth(width);
    };

    const raf = requestAnimationFrame(compute);
    const onResize = () => requestAnimationFrame(compute);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [renderLinearBarcode, usesJsBarcode, usesBwipBarcode, normalizedText, albumIndex, currentImage.url]);

  useEffect(() => {
    let mounted = true;
    setAlbumLoading(true);
    setAlbumError(null);
    ensureStorage()
      .then((storageInstance) => {
        if (!mounted) return;
        if (!storageInstance) {
          setAlbumError("Storage service unavailable.");
          setAlbumLoading(false);
          return;
        }
        const folderRef = ref(storageInstance, "Demo Pics");
        setAlbumLoading(true);
        setAlbumError(null);
        return listAll(folderRef)
          .then(async (res: ListResult) => {
            const items = res.items;
            const imageEntries: TrainingImage[] = await Promise.all(
              items.map(async (itemRef: StorageReference) => {
                const url = await getDownloadURL(itemRef);
                const metadata = await getMetadata(itemRef).catch(() => undefined);
                const overlayCornerKey = metadata
                  ? cornerMetadataToState[metadata.customMetadata?.overlayCorner?.toLowerCase() ?? ""]
                  : undefined;
                const preferredCodeType = metadata
                  ? codeTypeMetadataToState(
                      metadata.customMetadata?.overlayCodeType ?? metadata.customMetadata?.codeType
                    )
                  : undefined;
                return {
                  title: itemRef.name,
                  url,
                  overlayCornerKey,
                  preferredCodeType,
                };
              })
            );
            if (!mounted) return;
            setAlbumImages(imageEntries);
            setAlbumIndex(0);
            if (imageEntries.length === 0) {
              setAlbumError("No images found in Demo Pics");
            }
          })
          .catch((err: unknown) => {
            console.error("[ScanSandbox] Failed to load album", err);
            if (!mounted) return;
            setAlbumError("Failed to load Demo Pics album");
          })
          .finally(() => {
            if (!mounted) return;
            setAlbumLoading(false);
          });
      })
      .catch((err) => {
        console.error("[ScanSandbox] ensureStorage rejected", err);
        if (!mounted) return;
        setAlbumError("Storage initialization failed");
        setAlbumLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setNextOverlayCorner(currentImage?.overlayCornerKey ?? null);
    setNextCodeType(currentImage?.preferredCodeType ?? null);
  }, [albumIndex, currentImage?.overlayCornerKey, currentImage?.preferredCodeType]);

  const overlayCornerStyles = useMemo(() => {
    switch (overlayCorner) {
      case "tl":
        return { top: 16, left: 16 } as const;
      case "tr":
        return { top: 16, right: 16 } as const;
      case "bl":
        return { bottom: 16, left: 16 } as const;
      case "br":
      default:
        return { bottom: 16, right: 16 } as const;
    }
  }, [overlayCorner]);

  const handleTypeChange = (e: SelectChangeEvent) => {
    setCodeType(e.target.value as CodeType);
  };

  const handleCornerChange = (e: SelectChangeEvent) => {
    const value = e.target.value as OverlayCornerKey;
    setOverlayCorner(value);
  };

  const handleNextImage = () => {
    setAlbumIndex((prev) => {
      if (albumImages.length === 0) return prev;
      return (prev + 1) % albumImages.length;
    });
    if (cyclingRows.length > 0) {
      setPendingItemIndex((prev) => {
        const baseIndex = prev ?? currentItemIndex;
        return baseIndex + 1;
      });
    }
  };

  const handlePrevImage = () => {
    setAlbumIndex((prev) => {
      if (albumImages.length === 0) return prev;
      return (prev - 1 + albumImages.length) % albumImages.length;
    });
    if (cyclingRows.length > 0) {
      setPendingItemIndex((prev) => {
        const baseIndex = prev ?? currentItemIndex;
        return baseIndex - 1;
      });
    }
  };

  const shipmentsById = useMemo(() => {
    const map = new Map<string, Shipment>();
    shipments.forEach((shipment) => {
      map.set(shipment.id, shipment);
    });
    return map;
  }, [shipments]);

  const shipmentOptions = useMemo(
    () =>
      possibleShipmentIds
        .filter((id) => {
          const s: any = shipmentsById.get(id);
          if (!s) return false;
          const hasOrderIds = Array.isArray(s?.orderIds) && s.orderIds.length > 0;
          const snap = s?.ordersSnapshot || {};
          const hasSnapshot = Object.keys(snap).length > 0;
          return hasOrderIds || hasSnapshot;
        })
        .map((id) => {
        const shipment = shipmentsById.get(id);
        if (!shipment) {
          return { id, label: id };
        }
        let dateLabel = "";
        try {
          const rawDate: any = (shipment as any)?.shipmentDate;
          const jsDate =
            rawDate && typeof rawDate.toDate === "function"
              ? rawDate.toDate()
              : rawDate instanceof Date
              ? rawDate
              : null;
          if (jsDate) {
            dateLabel = jsDate.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          }
        } catch {
          // ignore formatting issues
        }
        const statusRaw = (shipment as any)?.status as string | undefined;
        const statusLabel = formatStatusLabel(statusRaw);
        const parts = [id];
        if (dateLabel) parts.push(dateLabel);
        if (statusLabel) parts.push(statusLabel);
        return { id, label: parts.join(" · ") };
      }),
    [possibleShipmentIds, shipmentsById]
  );

  const processedCount = useMemo(
    () =>
      Object.values(processedGroups || {}).reduce(
        (total, items) => total + (items?.length ?? 0),
        0
      ),
    [processedGroups]
  );
  const unprocessedCount = useMemo(
    () =>
      Object.values(unprocessedGroups || {}).reduce(
        (total, items) => total + (items?.length ?? 0),
        0
      ),
    [unprocessedGroups]
  );
  const totalCount = processedCount + unprocessedCount;

  const { tableSections, cyclingRows } = useMemo(() => {
    const buildRows = (
      groups: Record<string, ItemSnapshot[]>,
      isProcessed: boolean
    ): SandboxRow[] =>
      Object.entries(groups || {}).flatMap(([orderId, items]) =>
        (items || []).map((item) => ({
          key: `${isProcessed ? "processed" : "unprocessed"}-${orderId}-${item.id}`,
          orderLabel: deriveOrderLabel(orderId, item),
          itemLabel: item.barcodeId || item.id,
          destination: item.destination || "N/A",
          status: item.status,
          processed: isProcessed,
          cabinetNumber: item.cabinetNumber,
          itemId: item.id,
        }))
      );

    const outstandingRows = buildRows(unprocessedGroups, false);
    const processedRows = buildRows(processedGroups, true);
    const combinedRows = [...outstandingRows, ...processedRows].sort((a, b) => {
      const aRank = extractNumericRank(a.itemLabel);
      const bRank = extractNumericRank(b.itemLabel);
      if (aRank !== bRank) return aRank - bRank;
      return a.itemLabel.localeCompare(b.itemLabel);
    });

    return {
      tableSections: [
        {
          title: "Outstanding Items",
          rows: outstandingRows,
        },
        {
          title: "Processed Items",
          rows: processedRows,
        },
      ],
      cyclingRows: combinedRows,
    };
  }, [processedGroups, unprocessedGroups]);

  const cycleToItem = useCallback(
    (nextIndex: number) => {
      if (!cyclingRows.length) return;
      const length = cyclingRows.length;
      const normalized =
        ((nextIndex % length) + length) % length;
      setCurrentItemIndex(normalized);
      const nextValue = cyclingRows[normalized]?.itemLabel;
      if (nextValue) {
        setText(nextValue);
      }
    },
    [cyclingRows]
  );

  useEffect(() => {
    if (cyclingRows.length > 0) {
      cycleToItem(0);
    } else {
      setCurrentItemIndex(0);
      setPendingItemIndex(null);
    }
  }, [cyclingRows, cycleToItem]);

  const handleShipmentSelect = (event: SelectChangeEvent<string>) => {
    const shipmentId = event.target.value;
    if (!shipmentId) {
      setSelectedShipmentIndex(-1);
      setUnloadLocation("");
      return;
    }
    const nextIndex = possibleShipmentIds.findIndex((id) => id === shipmentId);
    if (nextIndex !== -1) {
      setUnloadLocation("");
      setSelectedShipmentIndex(nextIndex);
    }
  };

  const handleLocationSelect = (event: SelectChangeEvent<string>) => {
    const location = event.target.value;
    setUnloadLocation(location);
  };

  const hasShipmentSelection = Boolean(selectedShipment);
  const hasLocationSelection = Boolean(unloadLocation);
  const shouldShowTable = hasShipmentSelection && hasLocationSelection;

  const activeRow = cyclingRows[currentItemIndex] ?? undefined;
  const activeOrderLabel = activeRow?.orderLabel;
  const activeItemDisplay = activeRow
    ? activeRow.cabinetNumber && activeRow.cabinetNumber.trim()
      ? activeRow.cabinetNumber
      : activeRow.itemLabel
    : undefined;
  const activeStatus = activeRow?.status;
  const activeStatusLabel = activeStatus ? toTitleCase(activeStatus) : undefined;
  const activeStatusColor = activeStatus ? getStatusChipColor(activeStatus) : "default";

  return (
    <DashboardLayout>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <QrCodeScannerIcon sx={{ fontSize: "2.5rem" }} color="primary" />
          <Typography variant="h4" sx={{ fontWeight: 600 }} color="primary">
            Try the Scan Experience
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary", maxWidth: 840 }}>
          Explore how Cabinet Scanner works in the field. Select a shipment and location, then start scanning. Photos and barcodes shown here are examples only — designed to help you imagine real-world use.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            mb: 3,
            alignItems: { xs: "stretch", sm: "center" },
            flexWrap: "wrap",
            rowGap: 1.5,
            columnGap: 2,
            maxWidth: { xs: "100%", xl: 1200 },
            mx: "auto",
          }}
        >
          <FormControl
            sx={{
              minWidth: 220,
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              ...selectControlBaseSx,
            }}
            size="small"
          >
            <InputLabel id="sandbox-shipment-label-top" shrink>
              Shipment
            </InputLabel>
            <Select
              labelId="sandbox-shipment-label-top"
              label="Shipment"
              value={selectedShipment || ""}
              onChange={handleShipmentSelect}
              displayEmpty
              renderValue={(value) => {
                const stringValue = (value ?? "") as string;
                if (!stringValue) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Select shipment
                    </Typography>
                  );
                }
                const resolved =
                  shipmentOptions.find((option) => option.id === stringValue)
                    ?.label ?? stringValue;
                return (
                  <Typography
                    variant="body2"
                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {resolved}
                  </Typography>
                );
              }}
            >
              <MenuItem value="" disabled>
                Select shipment
              </MenuItem>
              {shipmentOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              minWidth: 220,
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              ...selectControlBaseSx,
            }}
            size="small"
            disabled={!hasShipmentSelection}
          >
            <InputLabel id="sandbox-location-label-top" shrink>
              Location
            </InputLabel>
            <Select
              labelId="sandbox-location-label-top"
              label="Location"
              value={unloadLocation || ""}
              onChange={handleLocationSelect}
              displayEmpty
              renderValue={(value) => {
                const stringValue = (value ?? "") as string;
                if (!stringValue) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Select location
                    </Typography>
                  );
                }
                return (
                  <Typography
                    variant="body2"
                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {stringValue}
                  </Typography>
                );
              }}
            >
              <MenuItem value="" disabled>
                Select location
              </MenuItem>
              {possibleLocations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              ...selectControlBaseSx,
            }}
          >
            <InputLabel id="code-type-label">Code Type</InputLabel>
            <Select
              labelId="code-type-label" size="small"
              label="Code Type"
              value={codeType}
              onChange={handleTypeChange}
              renderValue={(value) => {
                const selectedOption = CODE_OPTIONS.find(
                  (opt): opt is Extract<CodeOption, { type: "option" }> =>
                    opt.type === "option" && opt.value === value
                );
                return selectedOption ? selectedOption.label : value;
              }}
              MenuProps={{
                MenuListProps: {
                  dense: true,
                },
              }}
            >
              {CODE_OPTIONS.map((option) => {
                if (option.type === "divider") {
                  return (
                    <MenuItem key={option.key} disabled dense sx={{ py: 0 }}>
                      <Divider sx={{ width: "100%" }} />
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* Manual input hidden in simulator mode */}
        </Stack>

        {/* Removed standalone generator preview; overlays below remain */}

        {/* Single image overlay with selectable corner */}
        <Box
          sx={{
            mt: 2,
            maxWidth: { xs: "100%", lg: 960, xl: 1200 },
            mx: "auto",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  {activeOrderLabel ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                      >
                        Order: {activeOrderLabel}
                      </Typography>
                      {activeItemDisplay ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.25, overflow: "hidden" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Item: {activeItemDisplay}
                          </Typography>
                          {activeStatusLabel ? (
                            <Chip
                              size="small"
                              color={activeStatusColor}
                              variant="outlined"
                              label={activeStatusLabel}
                              sx={{ flexShrink: 0 }}
                            />
                          ) : null}
                        </Stack>
                      ) : null}
                    </>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={handlePrevImage} disabled={albumImages.length <= 1}>
                    Previous
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleNextImage} disabled={albumImages.length <= 1}>
                    Next
                  </Button>
                </Stack>
              </Box>
              <Box
                ref={imageBoxRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "100%",
                  minHeight: { xs: 200, sm: 260 },
                }}
              >
                {albumLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <img
                    alt={currentImage.title}
                    src={currentImage.url}
                    style={{ width: "100%", borderRadius: 8, display: "block" }}
                    onLoad={() => {
                      if (renderLinearBarcode) {
                        setOverlayWidth(null);
                      }
                      if (nextOverlayCorner) {
                        setOverlayCorner(nextOverlayCorner);
                        setNextOverlayCorner(null);
                      }
                      if (nextCodeType) {
                        setCodeType(nextCodeType);
                        setNextCodeType(null);
                      }
                      if (pendingItemIndex !== null) {
                        cycleToItem(pendingItemIndex);
                        setPendingItemIndex(null);
                      }
                    }}
                  />
                )}
                {albumError && !albumLoading && (
                  <Typography variant="caption" color="error" sx={{ position: "absolute", bottom: 8, left: 8, bgcolor: "background.paper", px: 1, borderRadius: 0.5 }}>
                    {albumError}
                  </Typography>
                )}
                {/* Overlayed barcode/QR (small) */}
      <Box
        sx={{
          position: "absolute",
          ...overlayCornerStyles,
          bgcolor: "#fff",
          // For QR keep compact padding; for Code128-like formats use equal horizontal padding as quiet zone
          p: renderLinearBarcode ? 0 : 1,
          px: renderLinearBarcode ? `${QUIET_PAD}px` : undefined,
          py: renderLinearBarcode ? `${QUIET_PAD_VERTICAL}px` : undefined,
                    borderRadius: 1,
                    boxShadow: 3,
                    boxSizing: "border-box",
                    width: renderLinearBarcode
                      ? `${overlayWidth ?? 260}px`
                      : {
                        xs: "34%",
                        sm: "28%",
                        md: "24%",
                      },
                    minHeight: renderLinearBarcode ? `${LINEAR_BARCODE_HEIGHT}px` : undefined,
                    overflow: "hidden",
                    display: albumLoading ? "none" : "block",
                  }}
                >
                  {renderLinearBarcode ? (
                    usesJsBarcode ? (
                      <Box>
                        <svg ref={overlaySvgRef} style={{ width: "100%", height: "auto", display: "block" }} />
                      </Box>
                    ) : (
                      <Box>
                        <canvas
                          ref={barcodeCanvasRef}
                          style={{ width: "100%", height: "auto", display: "block" }}
                        />
                      </Box>
                    )
                  ) : (
                    <Box>
                      <QRCode value={normalizedText} style={{ width: "100%", height: "auto", display: "block" }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            mt: 4,
            maxWidth: { xs: "100%", lg: 960, xl: 1200 },
            mx: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
            <TableChartIcon sx={{ fontSize: "2.25rem" }} color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }} color="primary">
              Shipment Progress Sandbox
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <FormControl
              sx={{ minWidth: 220, ...selectControlBaseSx }}
              size="small"
            >
              <InputLabel id="sandbox-shipment-label" shrink>
                Shipment
              </InputLabel>
              <Select
                labelId="sandbox-shipment-label"
                label="Shipment"
                value={selectedShipment || ""}
                onChange={handleShipmentSelect}
                displayEmpty
                renderValue={(value) => {
                  const stringValue = (value ?? "") as string;
                  if (!stringValue) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Select shipment
                      </Typography>
                    );
                  }
                  const resolved =
                    shipmentOptions.find((option) => option.id === stringValue)
                      ?.label ?? stringValue;
                  return (
                    <Typography
                      variant="body2"
                      sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {resolved}
                    </Typography>
                  );
                }}
              >
                <MenuItem value="" disabled>
                  Select shipment
                </MenuItem>
                {shipmentOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{ minWidth: 220, ...selectControlBaseSx }}
              size="small"
              disabled={!hasShipmentSelection}
            >
              <InputLabel id="sandbox-location-label" shrink>
                Location
              </InputLabel>
              <Select
                labelId="sandbox-location-label"
                label="Location"
                value={unloadLocation || ""}
                onChange={handleLocationSelect}
                displayEmpty
                renderValue={(value) => {
                  const stringValue = (value ?? "") as string;
                  if (!stringValue) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Select location
                      </Typography>
                    );
                  }
                  return (
                    <Typography
                      variant="body2"
                      sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {stringValue}
                    </Typography>
                  );
                }}
              >
                <MenuItem value="" disabled>
                  Select location
                </MenuItem>
                {possibleLocations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {shouldShowTable && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
            >
              <Chip
                label={`Processed: ${processedCount}`}
                color="success"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Outstanding: ${unprocessedCount}`}
                color={unprocessedCount ? "warning" : "default"}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Total: ${totalCount}`}
                color="info"
                variant="outlined"
                size="small"
              />
            </Stack>
          )}

          {shouldShowTable ? (
            totalCount > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ width: "100%" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Destination
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableSections.map((section) =>
                      section.rows.length > 0 ? (
                        <React.Fragment key={section.title}>
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{
                                fontWeight: 600,
                                backgroundColor: "action.hover",
                              }}
                            >
                              {section.title}
                            </TableCell>
                          </TableRow>
                          {section.rows.map((row) => (
                            <TableRow key={row.key}>
                              <TableCell>{row.orderLabel}</TableCell>
                              <TableCell>{row.itemLabel}</TableCell>
                              <TableCell>{row.destination}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={getStatusChipColor(row.status)}
                                  variant="outlined"
                                  label={toTitleCase(row.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={row.processed ? "success" : "warning"}
                                  variant={row.processed ? "filled" : "outlined"}
                                  label={
                                    row.processed ? "Processed" : "Outstanding"
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ) : null
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  No items match this shipment and location yet.
                </Typography>
              </Paper>
            )
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Select a shipment and location to view current items.
              </Typography>
            </Paper>
          )}
        </Box>

      </Box>
    </DashboardLayout>
  );
};

export default ScanSandbox;
